import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useCompany } from './CompanyContext';
import { permissionService } from '../services/permissionService';

const PermissionContext = createContext();

/**
 * Hook to access permission context
 * @returns {Object} Permission context with hasPermission, loading, etc.
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

/**
 * Permission Provider Component
 * Manages permission state for the current user in the current company
 */
export const PermissionProvider = ({ children }) => {
  const { currentCompany } = useCompany();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);

  /**
   * Load permissions for the current company
   */
  const loadPermissions = useCallback(async () => {
    if (!currentCompany?.id) {
      setPermissions({});
      setUserRole(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await permissionService.getUserPermissions(currentCompany.id);

      // Convert permissions array to object for easy lookup
      const permMap = {};
      (result.permissions || []).forEach(p => {
        permMap[p.code] = {
          granted: p.granted,
          source: p.source,
          reason: p.reason,
          name: p.name,
          description: p.description,
          category: p.category
        };
      });

      setPermissions(permMap);
      setUserRole(result.role);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Failed to load permissions');
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  // Reload permissions when company changes
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  /**
   * Check if user has a specific permission
   * @param {string} code - Permission code
   * @returns {boolean}
   */
  const hasPermission = useCallback((code) => {
    return permissions[code]?.granted === true;
  }, [permissions]);

  /**
   * Check if user has ALL of the specified permissions
   * @param {string[]} codes - Array of permission codes
   * @returns {boolean}
   */
  const hasAllPermissions = useCallback((codes) => {
    return codes.every(code => hasPermission(code));
  }, [hasPermission]);

  /**
   * Check if user has ANY of the specified permissions
   * @param {string[]} codes - Array of permission codes
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback((codes) => {
    return codes.some(code => hasPermission(code));
  }, [hasPermission]);

  /**
   * Get the source of a permission (role_default or override)
   * @param {string} code - Permission code
   * @returns {string|null}
   */
  const getPermissionSource = useCallback((code) => {
    return permissions[code]?.source || null;
  }, [permissions]);

  /**
   * Get full permission info
   * @param {string} code - Permission code
   * @returns {Object|null}
   */
  const getPermissionInfo = useCallback((code) => {
    return permissions[code] || null;
  }, [permissions]);

  /**
   * Check if user can view workforce (employees or contractors)
   * Convenience method for common check
   */
  const canViewWorkforce = useMemo(() => {
    return hasPermission('view_employees') || hasPermission('view_contractors');
  }, [hasPermission]);

  /**
   * Check if user can manage workforce
   * Convenience method for common check
   */
  const canManageWorkforce = useMemo(() => {
    return hasPermission('manage_employees') || hasPermission('manage_contractors');
  }, [hasPermission]);

  /**
   * Check if user can view any jobs
   * Convenience method for common check
   * Note: view_public_jobs removed - all jobs are now private
   */
  const canViewJobs = useMemo(() => {
    return hasAnyPermission(['view_all_jobs', 'view_assigned_jobs']);
  }, [hasAnyPermission]);

  /**
   * Check if user can manage jobs
   * Convenience method for common check
   */
  const canManageJobs = useMemo(() => {
    return hasPermission('manage_jobs');
  }, [hasPermission]);

  // Inspection convenience checks
  const canViewTemplates = useMemo(() => hasPermission('view_inspection_templates'), [hasPermission]);
  const canCreateTemplates = useMemo(() => hasPermission('create_inspection_templates'), [hasPermission]);
  const canManageTemplates = useMemo(() => hasPermission('manage_inspection_templates'), [hasPermission]);
  const canScheduleInspections = useMemo(() => hasPermission('schedule_inspections'), [hasPermission]);
  const canConductInspections = useMemo(() => hasPermission('conduct_inspections'), [hasPermission]);
  const canApproveInspections = useMemo(() => hasPermission('approve_inspections'), [hasPermission]);

  const value = useMemo(() => ({
    // State
    permissions,
    loading,
    error,
    userRole,

    // Core permission checks
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    getPermissionSource,
    getPermissionInfo,

    // Convenience checks
    canViewWorkforce,
    canManageWorkforce,
    canViewJobs,
    canManageJobs,

    // Inspection convenience checks
    canViewTemplates,
    canCreateTemplates,
    canManageTemplates,
    canScheduleInspections,
    canConductInspections,
    canApproveInspections,

    // Actions
    refresh: loadPermissions
  }), [
    permissions,
    loading,
    error,
    userRole,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    getPermissionSource,
    getPermissionInfo,
    canViewWorkforce,
    canManageWorkforce,
    canViewJobs,
    canManageJobs,
    canViewTemplates,
    canCreateTemplates,
    canManageTemplates,
    canScheduleInspections,
    canConductInspections,
    canApproveInspections,
    loadPermissions
  ]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
